import { useCallback } from "react"
import config from "@root/config.js"
import type { IpapiLocationLookupResponse } from "@root/types/global"
import { useStorage } from "@app/hooks/useCore"

export const useIPAPI = () => {
  const { getStorage, setStorage, removeStorage } = useStorage()

  // IPAPI caching configuration
  const IPAPI_CACHE_KEY = config.settings.keys.ipapiCacheKey
  const IPAPI_CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

  /**
   * Get cached IPAPI response from useStorage
   * @returns Cached IPAPI response or null if not found/expired
   */
  const getCachedIpapiResponse = useCallback((): IpapiLocationLookupResponse | null => {
    try {
      if (typeof window === "undefined") return null // SSR safety

      const cached = getStorage(IPAPI_CACHE_KEY)
      if (!cached) return null

      const { data, timestamp } = JSON.parse(cached)
      const now = Date.now()

      // Check if cache is expired
      if (now - timestamp > IPAPI_CACHE_DURATION) {
        removeStorage(IPAPI_CACHE_KEY)
        return null
      }

      return data
    } catch (error) {
      console.error("Error reading IPAPI cache:", error)
      if (typeof window !== "undefined") {
        removeStorage(IPAPI_CACHE_KEY)
      }
      return null
    }
  }, [IPAPI_CACHE_DURATION, IPAPI_CACHE_KEY, getStorage, removeStorage])

  /**
   * Save IPAPI response to useStorage cache
   * @param data - IPAPI response data to cache
   */
  const setCachedIpapiResponse = useCallback(
    (data: IpapiLocationLookupResponse): void => {
      try {
        if (typeof window === "undefined") return // SSR safety

        const cacheData = {
          data,
          timestamp: Date.now(),
        }
        setStorage(IPAPI_CACHE_KEY, JSON.stringify(cacheData))
      } catch (error) {
        console.error("Error saving IPAPI cache:", error)
      }
    },
    [IPAPI_CACHE_KEY, setStorage]
  )

  /**
   * Fetch IPAPI response with caching
   * @param endpoint - IPAPI endpoint URL
   * @returns Promise resolving to IPAPI response
   */
  const fetchIpapiResponse = useCallback(
    async (endpoint: string): Promise<IpapiLocationLookupResponse> => {
      // Check cache first
      const cached = getCachedIpapiResponse()

      if (cached) {
        return cached
      }

      // Fetch from API if not cached
      try {
        const response = await fetch(endpoint)

        if (!response.ok) {
          throw new Error(`IPAPI request failed: ${response.status} ${response.statusText}`)
        }

        const data = (await response.json()) as IpapiLocationLookupResponse

        // Cache the response
        setCachedIpapiResponse(data)

        return data
      } catch (error) {
        console.error("Error fetching IPAPI response:", error)
        throw error
      }
    },
    [getCachedIpapiResponse, setCachedIpapiResponse]
  )

  return { fetchIpapiResponse }
}
